import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface Pergunta {
    id: number
    descricao: string
    ordem: number | null
    tipo: number
    ativo: boolean
}

export interface SalvarPerguntasPayload {
    perguntas: { id: number | null; descricao: string; ordem: number | null; tipo: number }[]
    deletarIds: number[]
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaErroRaw = { codErro: number; mensagem?: string }

function isErroBackend(data: unknown): data is RespostaErroRaw {
    return (
        typeof data === "object" &&
        data !== null &&
        !Array.isArray(data) &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    )
}

function erroParaRespostaRaw(error: unknown): RespostaErroRaw {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as RespostaErroRaw).codErro > 0
    ) {
        const d = data as RespostaErroRaw
        return { codErro: d.codErro, mensagem: d.mensagem }
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" }
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" }
}

export const obterPerguntas = async function (
    authorization: string
): Promise<Pergunta[] | ErroApi> {
    try {
        const response = await api
            .get<Pergunta[] | RespostaErroRaw>("avaliacao/ObterPerguntasFornecedor", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        if (response.data === null || response.data === undefined) {
            return []
        }
        return response.data as Pergunta[]
    } catch (error) {
        RegistrarLogErro(error, "ObterPerguntasFornecedor", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export interface PerguntaSalva {
    id: number
}

export const salvarPerguntas = async function (
    authorization: string,
    payload: SalvarPerguntasPayload
): Promise<PerguntaSalva[] | ErroApi> {
    try {
        const response = await api
            .post<PerguntaSalva[] | RespostaErroRaw>(
                "avaliacao/SalvarPerguntasFornecedor",
                payload,
                {
                    headers: { "Content-Type": "application/json", Authorization: authorization },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return (response.data as PerguntaSalva[]) ?? []
    } catch (error) {
        RegistrarLogErro(error, "SalvarPerguntasFornecedor", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export interface ItemPesquisa {
    pergunta: string
    otimo: number
    bom: number
    regular: number
    ruim: number
    pessimo: number
}

export interface ComentarioPesquisa {
    idRespostaQuestionario: number
    idCliente: number
    facebookId: string | null
    cliente: string
    email: string
    telefone: string | null
    sugestao: string | null
    dataFormatada: string
    data: number
    estabelecimento: string
}

export interface ResultadoPesquisa {
    itens: ItemPesquisa[]
    comentarios: ComentarioPesquisa[]
    total: number
}

export interface RespostaDetalhe {
    pergunta: string
    likert: number | null
    respostaTexto: string | null
}

export interface DetalhesResposta {
    idRespostaQuestionario: number
    cliente: string
    email: string
    telefone: string | null
    respostas: RespostaDetalhe[]
}

export interface LinhaCSV {
    IdResposta: string
    Data: string
    Estabelecimento: string
    Cliente: string
    "E-mail": string
    Telefone: string
    Pergunta: string
    Resposta: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

export const obterResultadosPesquisa = async function (
    authorization: string,
    inicio: number,
    fim: number
): Promise<ResultadoPesquisa | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ResultadoPesquisa>>(
                `avaliacao/ObterResultadosPesquisa?inicio=${inicio}&fim=${fim}`,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaRaw<ResultadoPesquisa> }
            })

        return tratamentoResponse<ResultadoPesquisa>(response, "ObterResultadosPesquisa")
    } catch (error) {
        RegistrarLogErro(error, "ObterResultadosPesquisa", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const obterDetalhesResposta = async function (
    authorization: string,
    idRespostaQuestionario: number
): Promise<DetalhesResposta | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<DetalhesResposta>>(
                `avaliacao/ObterDetalhesResposta/${idRespostaQuestionario}`,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaRaw<DetalhesResposta> }
            })

        return tratamentoResponse<DetalhesResposta>(response, "ObterDetalhesResposta")
    } catch (error) {
        RegistrarLogErro(error, "ObterDetalhesResposta", String(idRespostaQuestionario))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const exportarAvaliacoesCSV = async function (
    authorization: string,
    inicio: number,
    fim: number
): Promise<LinhaCSV[] | ErroApi> {
    try {
        const response = await api
            .get<LinhaCSV[] | RespostaErroRaw>(
                `avaliacao/ExportarAvaliacoesCSV?inicio=${inicio}&fim=${fim}`,
                { headers: { "Content-Type": "application/json", Authorization: authorization } }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        if (isErroBackend(response.data)) {
            return { retornoErro: response.data.codErro, mensagem: response.data.mensagem ?? "" }
        }
        return (response.data as LinhaCSV[]) ?? []
    } catch (error) {
        RegistrarLogErro(error, "ExportarAvaliacoesCSV", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
